import { StoreType } from "@faire/retailer-shared/lib/StoreType";
import onboardSubUser from "@faire/web-api/api/retailer/invitations/onboard/post";
import saveIncompleteSignupAttempt from "@faire/web-api/api/user/retailer-signup-attempt/post";
import createUser from "@faire/web-api/api/user/sign-up/post";
import addToWaitList from "@faire/web-api/api/user/waitlist/post";
import { trackSignupOwfMainSignupCompleteStoreProfileCtaWithFbForwardingClick } from "@faire/web-api/events/signup/click/owfMainSignupCompleteStoreProfileCtaWithFbForwarding";
import { ILaunchWithFaireSignUpDetails } from "@faire/web-api/indigofair/data/ILaunchWithFaireSignUpDetails";
import { IMatchedBusiness } from "@faire/web-api/indigofair/data/IMatchedBusiness";
import { INavMenuItem } from "@faire/web-api/indigofair/data/INavMenuItem";
import { IOnboardUserRequest } from "@faire/web-api/indigofair/data/IOnboardUserRequest";
import { IOnboardUserResponse } from "@faire/web-api/indigofair/data/IOnboardUserResponse";
import { IPixelCustomDataParameters } from "@faire/web-api/indigofair/data/IPixelCustomDataParameters";
import { IResellerCertificate } from "@faire/web-api/indigofair/data/IResellerCertificate";
import { IRetailer } from "@faire/web-api/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api/indigofair/data/IRetailerLaunchWithFaireApplication";
import { ISignUpAttribution } from "@faire/web-api/indigofair/data/ISignUpAttribution";
import { ISignUpRequest } from "@faire/web-api/indigofair/data/ISignUpRequest";
import { ISignUpResponse } from "@faire/web-api/indigofair/data/ISignUpResponse";
import { IStyleQuiz } from "@faire/web-api/indigofair/data/IStyleQuiz";
import { IUser } from "@faire/web-api/indigofair/data/IUser";
import { QueryParameters as SignUpParams } from "@faire/web-api/routes/welcome/r";
import { WebApiError } from "@faire/web-api/WebApiError";
import { DEFAULT_LOCALE_KEY } from "@faire/web/common/consts/DEFAULT_LOCALE";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { localeKeyToLocaleString } from "@faire/web/common/localization/localeKeyToLocaleString";
import { makeObservable } from "@faire/web/common/makeObservable";
import { Storage } from "@faire/web/common/Storage";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import isArray from "lodash/isArray";
import once from "lodash/once";
import some from "lodash/some";
import {
  action,
  computed,
  IReactionDisposer,
  observable,
  reaction,
  toJS,
} from "mobx";

import {
  clearLoggedOutSessionActivity,
  getLandingActivity,
  getSignupActivity,
} from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { OWF_REFERRAL_CODE_KEY } from "@faire/retailer/components/OpenWithFaire/OpenWithFaireR2R/useCreateOwFR2RLinkEffect";
import { PhoneNumberValidator } from "@faire/retailer/components/PhoneInput/phoneNumberValidator";
import {
  isInvalidBusinessDescription,
  isInvalidBusinessType,
  isInvalidCountry,
  isInvalidEmail,
  isInvalidLocaleKey,
  isInvalidName,
  isInvalidNumberValue,
  isInvalidPassword,
  isInvalidPostalCode,
  isInvalidStore,
  isInvalidValue,
  isInvalidWebsite,
} from "@faire/retailer/components/SignUpForm/validate";
import { BUSINESS_TYPE } from "@faire/retailer/consts/BUSINESS_TYPE";
import { getLocaleKeyFromLocaleString } from "@faire/retailer/consts/LANGUAGE";
import { SIGN_UP_INPUT_FIELD_ERROR } from "@faire/retailer/consts/sign-up-input-field-error";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import {
  FaireDirectSignUpTypes,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { AppHistory } from "@faire/retailer/packages/core/services/AppHistory";
import { home } from "@faire/retailer/paths";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { getSettingMarketingSMSEligibleCountries } from "@faire/retailer/settings/getSettingMarketingSMSEligibleCountries";
import { isSupportedCountry } from "@faire/retailer/settings/getSettingSupportedCountries";
import { ElevatePortalStore } from "@faire/retailer/stores/domain/ElevatePortalStore";
import { OpenWithFaireStore } from "@faire/retailer/stores/domain/OpenWithFaireStore";
import { SSOStore } from "@faire/retailer/stores/domain/SSOStore";
import { ResellerInfoState } from "@faire/retailer/views/confirm-identity/steps/ResellerInfo/State";

import { shouldShowOwfOpeningMonth } from "../OpenWithFaire/OpenWithFaireApplication/utils";

import { IncomeRangesStore } from "./IncomeRangesStore";

type LeaseSigned = keyof typeof IRetailerLaunchWithFaireApplication.LeaseSigned;

// FIXME(refactor) This code should be refactored as the prop name does not match what the value expected
interface IFormField<ValueType> {
  value: ValueType;
  isInvalid: false | SIGN_UP_INPUT_FIELD_ERROR;
  setError: (error: string | false | undefined) => void;
  error?: string;
  selectValue: (value: ValueType) => void;
}

export class FormField<ValueType> implements IFormField<ValueType> {
  private readonly eventName: string;

  firstChangeEvent = once((query: string = "") =>
    trackEvent(`/open/${this.eventPrefix}/change/${this.eventName}${query}`)
  );

  @observable
  value: ValueType;

  @observable
  error?: string;

  @computed
  get isInvalid() {
    return this.validator(this.value);
  }

  constructor(
    value: ValueType,
    eventName: string,
    private validator: (value: ValueType) => SIGN_UP_INPUT_FIELD_ERROR | false,
    private eventPrefix: string = "sign-up"
  ) {
    makeObservable(this);
    this.value = value;
    this.eventName = eventName;

    const shouldFireEvent = isArray(value) ? value.length !== 0 : value;
    if (shouldFireEvent) {
      this.firstChangeEvent("?prefill=true");
    }
  }

  @action
  selectValue = (value: ValueType) => {
    this.firstChangeEvent();

    this.value = value;
    this.error = undefined;
  };

  @action
  setValue = (_name: string | undefined, value: ValueType) => {
    this.selectValue(value);
  };

  @action
  resetValue = (value: ValueType) => {
    this.value = value;
    this.error = undefined;
  };

  @action
  setError = (error: string | undefined | false) => {
    this.error = error ? error : undefined;
  };
}

// eslint-disable-next-line @faire/one-exported-class-per-file
export class SignUpFormState {
  resellerInfoState: ResellerInfoState;

  @observable
  serverErrorMessage: string = "";

  // Common Fields

  firstName: FormField<string>;

  lastName: FormField<string>;

  email: FormField<string>;

  password: FormField<string>;

  country: FormField<string>;

  phoneNumber: FormField<string>;

  localeKey: FormField<string>;

  businessType: FormField<BUSINESS_TYPE | undefined>;

  storeName: FormField<string>;

  website: FormField<string>;

  creationYear: FormField<
    keyof typeof IRetailer.ReportedCreationTime | undefined
  >;

  annualSales: FormField<string | undefined>;

  // Only set for OPENING_SOON
  storeOpeningDate: FormField<number | undefined>;

  // Fields that are unique by business type

  // Only set for BRICK_AND_MORTAR_SHOP or POP_UP_STORE
  postalCode: FormField<string>;

  // Only set for OTHER
  businessDescription: FormField<string>;

  // Only set for BRICK_AND_MORTAR_SHOP and OPENING_SOON
  leaseSigned: FormField<LeaseSigned | undefined>;

  // Style quiz "how do you best describe your store"
  @observable
  storeDescription: FormField<StoreType | undefined>;

  // Style quiz input for storeDescription type OTHER
  @observable
  otherStoreDescription: FormField<string>;

  // Style quiz categories to power recommendations. User must select at least one
  @observable
  selectedC1Items: FormField<INavMenuItem[]>;

  // Set if a user selected a business from a dropdown as opposed to freehand
  // typing in their business information.
  @observable
  matchedBusiness?: IMatchedBusiness;

  // Set to true if the user has given up trying to set matchedBusiness and instead
  // just want to manually input their store name and zip code
  @observable
  brickAndMortarAskForPostalCode: boolean = false;

  @observable
  receiveMarketingEmails: boolean = true;

  @observable
  receiveMarketingSMSs: boolean = false;

  @observable
  showWaitListModal: boolean = false;

  phoneNumberValidator = new PhoneNumberValidator();

  private disposers: IReactionDisposer[] = [];

  constructor(
    private strictLocalize: StrictLocalizeFunction,
    defaultFirstName?: string,
    defaultLastName?: string,
    defaultEmail?: string,
    defaultStoreName: string = "",
    defaultGooglePlaceId: string = "",
    defaultBusinessType?: BUSINESS_TYPE,
    defaultPhoneNumber?: string,
    defaultLocaleKey?: string,
    defaultCreationYear?: string,
    defaultStoreOpeningDate?: string,
    eventPrefix?: string,
    defaultCountry?: string
  ) {
    makeObservable(this);
    this.resellerInfoState = new ResellerInfoState(this.strictLocalize);
    this.firstName = new FormField(
      defaultFirstName || "",
      "first-name",
      isInvalidName
    );
    this.lastName = new FormField(
      defaultLastName || "",
      "last-name",
      isInvalidName
    );
    this.email = new FormField(
      defaultEmail || "",
      "email",
      isInvalidEmail,
      eventPrefix
    );
    this.password = new FormField<string>(
      "",
      "password",
      isInvalidPassword,
      eventPrefix
    );
    this.country = new FormField<string>(
      defaultCountry ||
        getSessionCountry() ||
        THREE_LETTER_PRIMARY_COUNTRY_CODES.USA,
      "country",
      isInvalidCountry,
      eventPrefix
    );
    this.phoneNumber = new FormField<string>(
      defaultPhoneNumber || "",
      "phone-number",
      () => false,
      eventPrefix
    );
    this.localeKey = new FormField<string>(
      defaultLocaleKey ??
        localeKeyToLocaleString({
          localeKey: DEFAULT_LOCALE_KEY,
          dashFormat: false,
        }),
      "language",
      isInvalidLocaleKey,
      eventPrefix
    );
    this.businessType = new FormField(
      defaultBusinessType,
      "business-type",
      (value) => isInvalidBusinessType(value!),
      eventPrefix
    );
    this.storeName = new FormField(
      "",
      "store",
      (value) => isInvalidStore(value),
      eventPrefix
    );

    if (defaultStoreName !== "") {
      if (defaultGooglePlaceId !== "") {
        this.businessType.selectValue(BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP);
        this.setMatchedBusiness(
          IMatchedBusiness.build({
            id: defaultGooglePlaceId,
            name: defaultStoreName,
            type: IMatchedBusiness.Type.GOOGLE_PLACES,
          })
        );
      }
      this.storeName.selectValue(defaultStoreName);
    }

    this.postalCode = new FormField(
      "",
      "postal-code",
      (value) =>
        (this.isBrickAndMortarSelected &&
          this.brickAndMortarAskForPostalCode) ||
        this.businessType.value === BUSINESS_TYPE.POP_UP_STORE
          ? isInvalidPostalCode(value, this.strictLocalize, this.country.value)
          : false,
      eventPrefix
    );

    this.annualSales = new FormField<string | undefined>(
      undefined,
      "annual-sales",
      this.annualSalesOrCreationYearValidator,
      eventPrefix
    );

    this.creationYear = new FormField<
      keyof typeof IRetailer.ReportedCreationTime | undefined
    >(
      defaultCreationYear
        ? // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
          IRetailer.ReportedCreationTime[defaultCreationYear]
        : undefined,
      "creation-year",
      this.annualSalesOrCreationYearValidator,
      eventPrefix
    );
    this.website = new FormField(
      "",
      "website",
      (value) =>
        this.businessType.value === BUSINESS_TYPE.ONLINE_ONLY ||
        !!this.website.value
          ? isInvalidWebsite(value)
          : false,
      eventPrefix
    );
    this.businessDescription = new FormField(
      "",
      "business-description",
      (value) =>
        this.businessType.value === BUSINESS_TYPE.OTHER
          ? isInvalidBusinessDescription(value)
          : false,
      eventPrefix
    );

    this.storeOpeningDate = new FormField<number | undefined>(
      defaultStoreOpeningDate ? Number(defaultStoreOpeningDate) : undefined,
      "store-opening-date",
      this.storeOpeningDateValidator,
      eventPrefix
    );

    this.leaseSigned = new FormField<LeaseSigned | undefined>(
      undefined,
      "lease-signed",
      this.leaseSignedValidator,
      eventPrefix
    );

    this.storeDescription = new FormField<StoreType | undefined>(
      undefined,
      "store-description",
      this.storeDescriptionValidator,
      eventPrefix
    );

    this.selectedC1Items = new FormField<INavMenuItem[]>(
      [],
      "selected-categories",
      this.selectedCategoriesValidator,
      eventPrefix
    );

    this.otherStoreDescription = new FormField<string>(
      "",
      "store-description-other",
      this.otherStoreDescriptionValidator,
      eventPrefix
    );

    // If the business type changes, reset the fields that are no longer
    // relevant to their default values.
    this.disposers.push(
      reaction(
        () => this.businessType.value,
        () => {
          if (!this.isBrickAndMortarSelected) {
            this.leaseSigned.resetValue(undefined);
          } else {
            /**
             * only reset store name when store type is changed to brick and mortar
             */
            this.storeName.resetValue("");
          }

          this.postalCode.resetValue("");
          this.website.resetValue(this.website.value);
          this.businessDescription.resetValue("");
          this.matchedBusiness = undefined;
          this.brickAndMortarAskForPostalCode = false;
        }
      ),
      reaction(
        () => this.creationYear.value,
        () => {
          if (!this.shouldShowLeaseSignedQuestion) {
            this.leaseSigned.resetValue(undefined);
          }
        }
      )
    );
  }

  private annualSalesOrCreationYearValidator = (
    value: string | undefined
  ): false | SIGN_UP_INPUT_FIELD_ERROR => {
    return isInvalidValue(value as string);
  };

  private storeDescriptionValidator = (value: StoreType | undefined) => {
    if (!value) {
      return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
    }
    return false;
  };

  private selectedCategoriesValidator = (value: INavMenuItem[]) => {
    if (!value.length) {
      return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
    }
    return false;
  };

  private otherStoreDescriptionValidator = (value: string) => {
    if (this.storeDescription.value === StoreType.OTHER && !value.length) {
      return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
    }
    return false;
  };

  private storeOpeningDateValidator = (
    value: number | undefined
  ): false | SIGN_UP_INPUT_FIELD_ERROR => {
    return this.shouldShowOpeningMonthQuestion
      ? isInvalidNumberValue(value)
      : false;
  };

  private leaseSignedValidator = (
    value: LeaseSigned | undefined
  ): false | SIGN_UP_INPUT_FIELD_ERROR => {
    return this.shouldShowLeaseSignedQuestion
      ? isInvalidValue(value as string)
      : false;
  };

  @computed
  private get formFields() {
    const fields: (
      | FormField<string>
      | FormField<string | undefined>
      | FormField<number>
      | FormField<number | undefined>
      | FormField<BUSINESS_TYPE | undefined>
      | FormField<ISignUpAttribution[]>
      | FormField<keyof typeof IRetailer.ReportedCreationTime | undefined>
      | FormField<LeaseSigned | undefined>
      | FormField<StoreType | undefined>
    )[] = [
      this.firstName,
      this.lastName,
      this.email,
      this.password,
      this.phoneNumber,
      this.businessType,
      this.storeName,
      this.postalCode,
      this.creationYear,
      this.annualSales,
      this.website,
      this.businessDescription,
      this.storeOpeningDate,
      this.leaseSigned,
      this.localeKey,
    ];

    return fields;
  }

  @computed
  private get subUserFormFields() {
    const fields: FormField<string>[] = [
      this.firstName,
      this.lastName,
      this.email,
      this.password,
      this.phoneNumber,
      this.localeKey,
    ];

    return fields;
  }

  @computed
  get isInvalid(): boolean {
    return this.formFields.some((formField) => formField.isInvalid);
  }

  @computed
  private get isRetailerSubUserInvalid(): boolean {
    return this.subUserFormFields.some((formField) => formField.isInvalid);
  }

  @computed
  get isCountryUSA(): boolean {
    return this.country.value === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;
  }

  @computed
  get isOpeningSoonSelected(): boolean {
    return (
      this.creationYear.value === IRetailer.ReportedCreationTime.OPENING_SOON
    );
  }

  @computed
  get isStartedThisYearSelected(): boolean {
    return (
      this.creationYear.value ===
      IRetailer.ReportedCreationTime.STARTED_THIS_YEAR
    );
  }

  @computed
  get isStartedLastYearSelected(): boolean {
    return (
      this.creationYear.value ===
      IRetailer.ReportedCreationTime.STARTED_ONE_YEAR_AGO
    );
  }

  @computed
  get shouldShowOpeningMonthQuestion(): boolean {
    return shouldShowOwfOpeningMonth(this.creationYear.value);
  }

  @computed
  get shouldShowLeaseSignedQuestion(): boolean {
    return (
      this.isBrickAndMortarSelected &&
      (this.isOpeningSoonSelected || this.isStartedThisYearSelected)
    );
  }

  @computed
  get isBrickAndMortarSelected(): boolean {
    return this.businessType.value === BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP;
  }

  @computed
  get hasLeaseSignedOrOwnStore(): boolean {
    const LeaseSigned = IRetailerLaunchWithFaireApplication.LeaseSigned;
    return (
      this.leaseSigned.value === LeaseSigned.YES ||
      this.leaseSigned.value === LeaseSigned.OWNED
    );
  }

  @action setMatchedBusiness = (matchedBusiness?: IMatchedBusiness) => {
    if (matchedBusiness) {
      this.storeName.selectValue(
        [matchedBusiness.name, matchedBusiness.location].join(", ")
      );
    }

    this.matchedBusiness = matchedBusiness;
  };

  @action setBrickAndMortarAskForPostalCode = () => {
    this.brickAndMortarAskForPostalCode = true;
  };

  resetErrors = () => {
    this.formFields.forEach((formField) => {
      formField.setError(undefined);
    });
  };

  @action validate = () => {
    this.formFields.forEach((formField) => {
      formField.setError(formField.isInvalid);
    });

    return !this.isInvalid;
  };

  @action private validateSubUser = () => {
    this.subUserFormFields.forEach((formField) => {
      formField.setError(formField.isInvalid);
    });

    return !this.isRetailerSubUserInvalid;
  };

  @computed
  private get businessTypePayload(): string | undefined {
    const businessType = this.businessType.value;
    let business_type: string | undefined;
    if (businessType === BUSINESS_TYPE.OTHER) {
      business_type = this.businessDescription.value
        ? this.businessDescription.value
        : BUSINESS_TYPE.OTHER;
    } else {
      business_type = this.businessType.value;
    }

    return business_type;
  }

  @computed
  private get partialUserPayload() {
    // all IUser fields collected BEFORE user creation (excluding password)
    return IUser.build({
      first_name: this.firstName.value || undefined,
      last_name: this.lastName.value || undefined,
      email_address: this.email.value || undefined,
      phone_number: this.phoneNumber.value || undefined,
      locale_key: getLocaleKeyFromLocaleString(this.localeKey.value),
    });
  }

  @computed
  private get partialSignUpRequest() {
    // all ISignUpRequest fields collected BEFORE user creation (excluding IUser)
    return {
      country: this.country.value,
      business_type: this.businessTypePayload,
      postal_code: this.postalCode.value || undefined,
    };
  }

  @computed
  private get fullUserPayload() {
    return IUser.build({
      ...this.partialUserPayload,
      password: this.password.value || undefined,
      roles: [],
      addresses: [],
      instruments: [],
    });
  }

  @computed
  private get subUserOnboardingPayload() {
    return IUser.build({
      ...this.fullUserPayload,
      is_sub_user: true,
    });
  }

  @computed
  private get styleQuizPayload(): IStyleQuiz {
    const selectedItems = this.selectedC1Items.value;
    const selectedCategories = selectedItems
      .map((item) => item.path_identifier)
      .filter(isNotUndefined);

    const otherStoreType =
      this.storeDescription.value === StoreType.OTHER
        ? this.otherStoreDescription.value
        : undefined;

    return IStyleQuiz.build({
      store_type: this.storeDescription.value,
      other_store_type: otherStoreType,
      category_path_identifiers: selectedCategories,
    });
  }

  @computed
  private get resellerCertPayload(): Partial<IResellerCertificate> | undefined {
    return some(
      this.resellerInfoState.payload,
      (value, key) => key !== "state" && !!value
    )
      ? {
          ...this.resellerInfoState.payload,
          signing_name: `${this.firstName.value} ${this.lastName.value}`,
        }
      : undefined;
  }

  saveIncompleteSignup = async (includeStyleQuiz?: boolean) => {
    const payload = ISignUpRequest.build({
      user: this.partialUserPayload,
      style_quiz: includeStyleQuiz ? this.styleQuizPayload : undefined,
      ...this.partialSignUpRequest,
    });
    try {
      await saveIncompleteSignupAttempt(payload);
    } catch (error) {
      // error handling by BE; background network task, user shouldn't know
      // this request failed
    }
  };

  private humanReadableErrorMessage = (error: unknown): string => {
    const genericFallback = this.strictLocalize({
      defaultMessage: "Something went wrong",
    });

    if (error instanceof WebApiError) {
      const status = error.httpStatusCode;

      if (!status || status >= 500) {
        return genericFallback;
      }

      return getWebApiErrorMessage(error, this.strictLocalize, genericFallback);
    }
    return genericFallback;
  };

  @action
  validateAndCreateUser = async (
    signUpParams: Partial<SignUpParams>
  ): Promise<ISignUpResponse | undefined> => {
    const fromFaireMarkets = signUpParams.source === "/markets";
    const fromOpenWithFaireLandingPage =
      signUpParams.source === "/open-with-faire";

    const website_url = this.website.value;

    let matchedBusiness: IMatchedBusiness;
    if (this.matchedBusiness) {
      matchedBusiness = toJS(this.matchedBusiness);
    } else {
      matchedBusiness = IMatchedBusiness.build({
        name: this.storeName.value,
        type: IMatchedBusiness.Type.MANUAL,
      });
    }

    const isFaireDirectCreditEligible = signUpParams.signUp
      ? FaireDirectSignUpTypes.includes(signUpParams.signUp as SignUpType)
      : false;

    const payload = ISignUpRequest.build({
      user: this.fullUserPayload,
      ...this.partialSignUpRequest,
      matched_business: matchedBusiness,
      reported_creation_time: this.creationYear.value,
      website_url,
      widget_token: signUpParams.widgetToken,
      annual_sales: this.annualSales.value
        ? ISignUpRequest.IAnnualSales.build(
            IncomeRangesStore.get().incomeRange(this.annualSales.value)
          )
        : undefined,
      marketing_email_preference: this.marketingEmailPreference,
      marketing_sms_preference: this.marketingSMSPreference,
      reseller_certificate: this.resellerCertPayload
        ? IResellerCertificate.build(this.resellerCertPayload)
        : undefined,
      store_opening_month: this.storeOpeningDate.value,
      launch_with_faire_sign_up_details: ILaunchWithFaireSignUpDetails.build({
        viewed_launch_with_faire_landing_page: fromOpenWithFaireLandingPage,
        referral_code: OpenWithFaireStore.get().canBeReferred(
          this.businessType.value
        )
          ? signUpParams.ref ?? Storage.session.getItem(OWF_REFERRAL_CODE_KEY)
          : undefined,
      }),
      style_quiz: this.styleQuizPayload,
      sso_token: SSOStore.get().isActive ? SSOStore.get().ssoToken : undefined,
      pre_sign_up_activities: getPreSignUpActivities(signUpParams.source),
      faire_direct_brand_alias:
        ElevatePortalStore.get().active && isFaireDirectCreditEligible
          ? ElevatePortalStore.get().faireDirectAlias
          : undefined,
    });

    if (fromFaireMarkets) {
      payload.attributions.push({
        type: ISignUpAttribution.SignUpAttributionType.FAIRE_MARKETS_PAGE,
        other_text: undefined,
      });
    }

    if (!this.validate()) {
      return undefined;
    }

    try {
      const response = await createUser(payload, {
        brand_invoice_token: signUpParams.brandInvoice,
      });

      if (fromOpenWithFaireLandingPage) {
        trackSignupOwfMainSignupCompleteStoreProfileCtaWithFbForwardingClick(
          IPixelCustomDataParameters.build({
            retailer_token: response.retailer?.token ?? "",
          })
        );
      }

      if (fromFaireMarkets) {
        trackEvent(`faire-markets/signup/retailer`);
      }

      clearLoggedOutSessionActivity();

      return { ...response };
    } catch (error) {
      let errorCode = "unknown";
      if (error instanceof WebApiError) {
        errorCode =
          error.responseData.service_error_code ??
          error.responseData.localized_message ??
          error.responseData.message ??
          errorCode;
      }

      trackEvent(`sign-up-page/submit-form/error/${errorCode}`);

      this.serverErrorMessage = this.humanReadableErrorMessage(error);

      return undefined;
    }
  };

  @action validateAndCreateSubUser = async (
    subUserInvitationToken: string
  ): Promise<IOnboardUserResponse | undefined> => {
    const payload = IOnboardUserRequest.build({
      user: this.subUserOnboardingPayload,
      invitation_token: subUserInvitationToken,
    });
    if (!this.validateSubUser()) {
      return undefined;
    }

    try {
      const response = await onboardSubUser(payload);
      return response;
    } catch (error) {
      this.serverErrorMessage = this.humanReadableErrorMessage(error);
      return undefined;
    }
  };

  private get formOrSessionCountry(): string | undefined {
    return this.country?.value ?? getSessionCountry();
  }

  @computed
  get shouldShowEmailOptOut(): boolean {
    // Only skip the opt out checkbox option for the USA
    // Otherwise, default to showing to be as compliant with GDPR as possible;
    return this.formOrSessionCountry !== THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;
  }

  private get marketingEmailPreference() {
    if (!this.shouldShowEmailOptOut) {
      return determineEmailOptInStatus();
    }

    return this.receiveMarketingEmails
      ? ISignUpRequest.EmailPreference.OPTED_IN
      : ISignUpRequest.EmailPreference.OPTED_OUT;
  }

  get marketingEmailCheckboxChecked(): boolean {
    return !this.receiveMarketingEmails;
  }

  @action
  toggleEmailPreferencesChanged = () => {
    this.receiveMarketingEmails = !this.receiveMarketingEmails;
  };

  @computed
  private get isEligibleCountryForMarketingSMS(): boolean {
    return getSettingMarketingSMSEligibleCountries().some(
      (eligibleCountry) => eligibleCountry === this.formOrSessionCountry
    );
  }

  @computed
  get shouldShowSMSOptIn(): boolean {
    return (
      !getIsBrandPreview() &&
      this.phoneNumber.value !== "" &&
      this.isEligibleCountryForMarketingSMS
    );
  }

  @computed
  private get marketingSMSPreference(): ISignUpRequest.SmsPreference {
    if (!this.isEligibleCountryForMarketingSMS) {
      return ISignUpRequest.SmsPreference.SMS_PREFERENCE_UNDECIDED;
    }

    return this.receiveMarketingSMSs
      ? ISignUpRequest.SmsPreference.SMS_PREFERENCE_OPTED_IN
      : ISignUpRequest.SmsPreference.SMS_PREFERENCE_OPTED_OUT;
  }

  @action
  toggleSMSPreferencesChanged = () => {
    this.receiveMarketingSMSs = !this.receiveMarketingSMSs;
  };

  signUpForWaitList = () =>
    addToWaitList({
      user_info: {
        email_address: this.email.value,
        first_name: this.firstName.value,
        last_name: this.lastName.value,
        country: this.country.value,
      },
    });

  get isWaitListModalApplicable() {
    const { value } = this.country;
    return value && !isSupportedCountry(value);
  }

  @action
  popWaitListModal = () => {
    this.showWaitListModal = true;
  };

  @action
  closeWaitListModal = () => {
    this.showWaitListModal = false;
    AppHistory.get().goTo(home());
  };

  cleanup = () => {
    this.disposers.forEach((dispose) => dispose());
    this.disposers = [];
  };
}

export default SignUpFormState;

const determineEmailOptInStatus = ():
  | ISignUpRequest.EmailPreference
  | undefined => {
  const { search = "" } = AppHistory.get().history.location;
  if (search.indexOf("brand-invoice") !== -1) {
    return ISignUpRequest.EmailPreference.UNDECIDED;
  }
  // default behaviour is to opt in
  return undefined;
};

const getPreSignUpActivities = (signUpSource: string = "") => {
  const preSignUpActivities = [
    getLandingActivity(),
    getSignupActivity(signUpSource),
  ].filter(isNotUndefined);
  return preSignUpActivities.length ? preSignUpActivities : undefined;
};
